import { NgModule } from "@angular/core";
import { PreloadAllModules, RouterModule, Routes } from "@angular/router";
import { AdminGuard } from "./guards/admin.guard";
import { AlreadyAuthenticatedGuard } from "./guards/alreadyauthenticated.guard";
import { LinkGuard } from "./guards/link.guard";
import { ProfileGuard } from "./guards/profile.guard";
import { AdministratorsResolverService } from "./resolvers/administrators.resolver";
import { AllMyOrdersResolverService } from "./resolvers/all-my-orders.resolver";
import { AreaCountersResolverService } from "./resolvers/area-counters.resolver";
import { AuthorityDetailsResolverService } from "./resolvers/authority-details.resolver";
import { AuthorityResolverService } from "./resolvers/authority.resolver";
import { CitiesResolverService } from "./resolvers/cities.resolver";
import { ConsumerDetailsResolverService } from "./resolvers/consumer-details.resolver";
import { ConsumerPlacesResolverService } from "./resolvers/consumer-places.resolver";
import { ConsumerShortdetailsResolverService } from "./resolvers/consumer-shortdetails.resolver";
import { ConsumersResolverService } from "./resolvers/consumers.resolver";
import { MarketResolverService } from "./resolvers/market.resolver";
import { MarketLightResolverService } from "./resolvers/marketLight.resolver";
import { MarketsResolverService } from "./resolvers/markets.resolver";
import { MyConsumersResolverService } from "./resolvers/my-consumers.resolver";
import { MyProducersResolverService } from "./resolvers/my-producers.resolver";
import { MyProductsResolverService } from "./resolvers/my-products.resolver";
import { MyResellersResolverService } from "./resolvers/my-resellers.resolver";
import { NotificationsResolverService } from "./resolvers/notifications.resolver";
import { OrderResolverService } from "./resolvers/order.resolver";
import { PaymentConfigResolverService } from "./resolvers/payment-config.resolver";
import { PaymentModesResolverService } from "./resolvers/paymentmodes.resolver";
import { ProducerCategoriesResolverService } from "./resolvers/producer-categories.resolver";
import { ProducerConsumerOrdersResolverService } from "./resolvers/producer-consumer-orders.resolver";
import { ProducerDetailsResolverService } from "./resolvers/producer-details.resolver";
import { ProducerOrderResolverService } from "./resolvers/producer-order.resolver";
import { ProducerPlacesResolverService } from "./resolvers/producer-places.resolver";
import { ProducerPrivateResolverService } from "./resolvers/producer-private.resolver";
import { ProducersResolverService } from "./resolvers/producers.resolver";
import { ProductResolverService } from "./resolvers/product.resolver";
import { ProductsByProducerResolverService } from "./resolvers/products-by-producer.resolver";
import { ProductsResolverService } from "./resolvers/products.resolver";
import { ProfileDetailsResolverService } from "./resolvers/profile-details.resolver";
import { ResellersByProducerResolverService } from "./resolvers/resellers-by-producer.resolver";
import { TourneeProducerResolverService } from "./resolvers/tournee-producer.resolver";
import { TourneeResolverService } from "./resolvers/tournee.resolver";
import { TourneesByProducerResolverService } from "./resolvers/tournees-by-producer.resolver";
import { UserNotificationsResolverService } from './resolvers/user-notifications.resolver';
import { UserResolverService } from "./resolvers/user.resolver";
import { UsersResolverService } from "./resolvers/users.resolver";
import { ViewerDetailsResolverService } from "./resolvers/viewer-details.resolver";

// const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['login']);
// const redirectLoggedInToHome = () => redirectLoggedInTo(['home']);

const routes: Routes = [
  { path: "", 
      canActivate: [AlreadyAuthenticatedGuard],
      loadChildren: () => import('./pages/login/login.module').then(m => m.LogInPageModule) },
  { path: "login", 
      canActivate: [AlreadyAuthenticatedGuard],
      loadChildren: () => import('./pages/login/login.module').then(m => m.LogInPageModule) },
  // { path: "home", 
  //     ...canActivate(redirectUnauthorizedToLogin),
  //     loadChildren: () => import('./pages/home/home.module').then(m => m.HomePageModule) },
  { path: "logout", redirectTo: "action?mode:logout"},
  { path: "delete-account", 
      canActivate: [ProfileGuard],
      resolve: {user:UserResolverService},
      loadChildren: () => import('./pages/account/account.module').then(m => m.AccountPageModule),
  },
  { path: "signup", 
      canActivate: [AlreadyAuthenticatedGuard],
      loadChildren: () => import('./pages/signup/signup.module').then(m => m.SignUpPageModule) },
  { path: "action", loadChildren: () => import('./pages/action/action.module').then(m => m.ActionPageModule) },
  { path: "oups", loadChildren: () => import('./pages/oups/oups.module').then(m => m.OupsPageModule) },
  { path: "legal", loadChildren: () => import('./pages/legal/legal.module').then(m => m.LegalPageModule) },
  {
    path: "authority/:profileId",
    canActivate: [ProfileGuard],
    data: { role: "authority" },
    resolve: {user:UserResolverService},
    children: [
      {
        path: "",
        loadChildren: () => import('./pages/home-authority/home-authority.module').then(m => m.HomeAuthorityPageModule),
        resolve: { markets: MarketsResolverService, authority: AuthorityDetailsResolverService }
      },
      {
        path: "profile",
        loadChildren: () => import('./pages/authority/authority.module').then(m => m.AuthorityPageModule),
        resolve: { item: AuthorityResolverService }
      },
      {
        path: "market",
        children: [
          {
            path: "new",
            loadChildren: () => import('./pages/market-edit/market-edit.module').then(m => m.MarketEditPageModule),
            resolve: { market: MarketLightResolverService, owner: AuthorityResolverService }
          },
          {
            path: ":marketId",
            children: [
              {
                path: "",
                loadChildren: () => import('./pages/market-view/market-view.module').then(m => m.MarketViewPageModule),
                resolve: { market: MarketLightResolverService, viewer: AuthorityResolverService, order: OrderResolverService }
              },
              {
                path: "edit",
                loadChildren: () => import('./pages/market-edit/market-edit.module').then(m => m.MarketEditPageModule),
                resolve: { market: MarketLightResolverService, owner: AuthorityResolverService }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: "consumer/:profileId",
    canActivate: [ProfileGuard],
    data: { role: "consumer" },
    resolve:{user:UserResolverService},
    children: [
      {
        path: "",
        loadChildren: () => import('./pages/home-consumer/home-consumer.module').then(m => m.HomeConsumerPageModule),
        resolve: { consumer: ConsumerDetailsResolverService, places:ConsumerPlacesResolverService }
      },
      { path: "profile", loadChildren: () => import('./pages/consumer/consumer.module').then(m => m.ConsumerPageModule), resolve: { item: ConsumerDetailsResolverService } },
      {
        path: "notifications",
        loadChildren: () => import('./pages/notifications/notifications.module').then(m => m.NotificationsPageModule),
        resolve: { items: UserNotificationsResolverService, owner: ConsumerDetailsResolverService }
      },
      {
        path: "order",
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/orders/orders.module').then(m => m.OrdersPageModule),
            resolve: { consumer: ConsumerDetailsResolverService }
          },
          { path: ":orderId", loadChildren: () => import('./pages/order/order.module').then(m => m.OrderPageModule), 
          resolve: { order: OrderResolverService, consumer: ConsumerDetailsResolverService } }
        ]
      }
    ]
  },
  // {
  //   path: "sale/:countryCode/:postalCode/:status/:tourneeId",
  //   loadChildren: "./pages/tournee-view/tournee-view.module#TourneeViewPageModule",
  //   resolve: { tournee: TourneeResolverService, order: OrderResolverService, consumer: ConsumerDetailsResolverService }
  // },
  {
    path: "sale",
    loadChildren: () => import('./pages/sales/sales.module').then(m => m.SalesPageModule),
    resolve: { consumer: ConsumerDetailsResolverService, places:ConsumerPlacesResolverService }
  },
  {
    path: "sale/:tourneeId",
    loadChildren: () => import('./pages/tournee-view/tournee-view.module').then(m => m.TourneeViewPageModule),
    resolve: { tournee: TourneeResolverService, order: OrderResolverService, consumer: ViewerDetailsResolverService, user: UserResolverService }
  },
  {
    path: "profile",
    resolve: {user:UserResolverService},
    children: [
      {
        path: "producer/:profileId",
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/producer-public/producer-public.module').then(m => m.ProducerPublicPageModule),
            resolve: {
              products: ProductsByProducerResolverService,
              tournees: TourneesByProducerResolverService,
              producer: ProducerDetailsResolverService,
              resellers: ResellersByProducerResolverService
            }
          }
        ]
      }
    ]
  },
  {
    path: "producer/:profileId",
    canActivate: [ProfileGuard],
    resolve: {user:UserResolverService},
    data: { role: "producer" },
    children: [
      {
        path: "",
        loadChildren: () => import('./pages/home-producer/home-producer.module').then(m => m.HomeProducerPageModule),
        resolve: { producer: ProducerDetailsResolverService }
      },
      {
        path: "profile",
        loadChildren: () => import('./pages/producer/producer.module').then(m => m.ProducerPageModule),
        resolve: {
          item: ProducerDetailsResolverService,
          itemPrivate: ProducerPrivateResolverService
        }
      },
      {
        path: "order",
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/orders/orders.module').then(m => m.OrdersPageModule),
            resolve: { orders: AllMyOrdersResolverService, consumer: ConsumerDetailsResolverService }
          },
          { path: ":orderId", loadChildren: () => import('./pages/order/order.module').then(m => m.OrderPageModule), 
            resolve: { order: OrderResolverService, consumer: ConsumerDetailsResolverService  } }
        ]
      },
      {
        path: "market",
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/markets/markets.module').then(m => m.MarketsPageModule),
            resolve: { viewer: ProducerDetailsResolverService, places: ProducerPlacesResolverService }
          },
          {
            path: "new",
            loadChildren: () => import('./pages/market-edit/market-edit.module').then(m => m.MarketEditPageModule),
            resolve: { market: MarketLightResolverService, owner: ProducerDetailsResolverService, places: ProducerPlacesResolverService}
          },
          {
            path: ":marketId",
            children: [
              {
                path: "",
                loadChildren: () => import('./pages/market-view/market-view.module').then(m => m.MarketViewPageModule),
                resolve: { market: MarketResolverService, viewer: ProducerDetailsResolverService, order: OrderResolverService }
              },
              {
                path: "deliver",
                loadChildren: () => import('./pages/market-manage/market-manage.module').then(m => m.MarketManagePageModule),
                resolve: { market: MarketResolverService, owner: ProducerDetailsResolverService }
              },
              {
                path: "edit",
                loadChildren: () => import('./pages/market-edit/market-edit.module').then(m => m.MarketEditPageModule),
                resolve: { market: MarketResolverService, owner: ProducerDetailsResolverService }
              },
              {
                path: "sale",
                children: [
                  {
                    path: "new",
                    loadChildren: () => import('./pages/tournee-edit/tournee-edit.module').then(m => m.TourneeEditPageModule),
                    resolve: {
                      market: MarketResolverService,
                      tournee: TourneeProducerResolverService,
                      producer: ProducerDetailsResolverService,
                      products: MyProductsResolverService,
                      places: ProducerPlacesResolverService,
                      paymentModes: PaymentConfigResolverService
                    }
                  },
                  {
                    path: ":tourneeId",
                    children: [
                      {
                        path: "",
                        loadChildren: () => import('./pages/tournee-tabs/tournee-tabs.module').then(m => m.TourneeTabsPageModule),
                      },
                      {
                        path: "tabs",
                        loadChildren: () => import('./pages/tournee-tabs/tournee-tabs.module').then(m => m.TourneeTabsPageModule),
                      },
                      {
                        path: "edit",
                        loadChildren: () => import('./pages/tournee-edit/tournee-edit.module').then(m => m.TourneeEditPageModule),
                        resolve: {
                          market: MarketResolverService,
                          tournee: TourneeProducerResolverService,
                          producer: ProducerDetailsResolverService,
                          products: MyProductsResolverService,
                          places: ProducerPlacesResolverService,
                          paymentModes: PaymentConfigResolverService
                        }
                      },
                      {
                        path: "map",
                        loadChildren: () => import('./pages/map/map.module').then(m => m.MapPageModule),
                        resolve: { market: MarketResolverService, tournee: TourneeProducerResolverService }
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: "sale",
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/tournees/tournees.module').then(m => m.TourneesPageModule),
            resolve: { producer: ProducerDetailsResolverService }
          },
          {
            path: "new",
            loadChildren: () => import('./pages/tournee-edit/tournee-edit.module').then(m => m.TourneeEditPageModule),
            resolve: {
              tournee: TourneeProducerResolverService,
              producer: ProducerDetailsResolverService,
              products: MyProductsResolverService,
              places: ProducerPlacesResolverService,
              paymentModes: PaymentConfigResolverService
            }
          },
          {
            path: ":tourneeId",
            children: [
              {
                path: "",
                loadChildren: () => import('./pages/tournee-tabs/tournee-tabs.module').then(m => m.TourneeTabsPageModule),
              },
              {
                path: "tabs",
                loadChildren: () => import('./pages/tournee-tabs/tournee-tabs.module').then(m => m.TourneeTabsPageModule),
              },
              {
                path: "order/new",
                loadChildren: () => import('./pages/tournee-view/tournee-view.module').then(m => m.TourneeViewPageModule),
                resolve: { tournee: TourneeResolverService, order: OrderResolverService, consumer: ViewerDetailsResolverService }
              },
              {
                path: "order/:orderId",
                loadChildren: () => import('./pages/order/order.module').then(m => m.OrderPageModule),
                resolve: { order: ProducerOrderResolverService, producer: ProducerDetailsResolverService }
              },
              {
                path: "product/:productId",
                loadChildren: () => import('./pages/product/product.module').then(m => m.ProductPageModule),
                resolve: { product: ProductResolverService, producer: ProducerDetailsResolverService, categories:ProducerCategoriesResolverService }
              },
              { path: "map", loadChildren: () => import('./pages/map/map.module').then(m => m.MapPageModule), resolve: { tournee: TourneeProducerResolverService } },
              {
                path: ":action",
                loadChildren: () => import('./pages/tournee-edit/tournee-edit.module').then(m => m.TourneeEditPageModule),
                resolve: {
                  tournee: TourneeProducerResolverService,
                  producer: ProducerDetailsResolverService,
                  products: MyProductsResolverService,
                  places: ProducerPlacesResolverService,
                  paymentModes: PaymentConfigResolverService
                }
              }
            ]
          }
        ]
      },
      {
        path: "product",
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/products-tabs/products-tabs.module').then(m => m.ProductsTabsPageModule),
          },
          {
            path: "new",
            loadChildren: () => import('./pages/product/product.module').then(m => m.ProductPageModule),
            resolve: { product: ProductResolverService, producer: ProducerDetailsResolverService, categories:ProducerCategoriesResolverService}
          },
          {
            path: ":productId",
            children: [
              {
                path: "",
                loadChildren: () => import('./pages/product/product.module').then(m => m.ProductPageModule),
                resolve: { product: ProductResolverService, producer: ProducerDetailsResolverService, categories:ProducerCategoriesResolverService}
              },
              {
                path: "edit",
                loadChildren: () => import('./pages/product/product.module').then(m => m.ProductPageModule),
                resolve: { product: ProductResolverService, producer: ProducerDetailsResolverService, categories:ProducerCategoriesResolverService}
              },
              {
                path: ":action",
                loadChildren: () => import('./pages/product/product.module').then(m => m.ProductPageModule),
                resolve: { product: ProductResolverService, producer: ProducerDetailsResolverService, categories:ProducerCategoriesResolverService}
              },
            ]
          }
        ]
      },
      {
        path: "reseller",
        data: { itemRole: "reseller" },
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/resellers-producer/resellers-producer.module').then(m => m.ResellersProducerPageModule),
            resolve: { items: MyResellersResolverService, owner: ProducerDetailsResolverService, user: UserResolverService }
          },
          {
            path: "new",
            loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfilePageModule),
            resolve: { item: ProfileDetailsResolverService, owner: ProducerDetailsResolverService, user: UserResolverService }
          },
          {
            path: ":itemId",
            children: [
              {
                path: "",
                loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfilePageModule),
                resolve: { item: ProfileDetailsResolverService, owner: ProducerDetailsResolverService, user: UserResolverService }
              },
              {
                path: "edit",
                loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfilePageModule),
                resolve: { item: ProfileDetailsResolverService, owner: ProducerDetailsResolverService, user: UserResolverService }
              }
            ]
          }
        ]
      },
      {
        path: "producer",
        data: { itemRole: "producer" },
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/producers/producers.module').then(m => m.ProducersPageModule),
            resolve: { owner: ProducerDetailsResolverService, producers:MyProducersResolverService, user: UserResolverService }
          },
          {
            path: "new",
            loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfilePageModule),
            resolve: { item: ProfileDetailsResolverService, owner: ProducerDetailsResolverService, user: UserResolverService }
          },
          {
            path: ":itemId",
            children: [
              {
                path: "",
                loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfilePageModule),
                resolve: { item: ProfileDetailsResolverService, owner: ProducerDetailsResolverService, user: UserResolverService }
              },
              {
                path: "edit",
                loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfilePageModule),
                resolve: { item: ProfileDetailsResolverService, owner: ProducerDetailsResolverService, user: UserResolverService }
              }
            ]
          }
        ]
      },
      {
        path: "administrator",
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/administrators/administrators.module').then(m => m.AdministratorsPageModule),
            resolve: { items: AdministratorsResolverService, producer: ProducerDetailsResolverService }
          }
        ]
      },
      {
        path: "notifications",
        loadChildren: () => import('./pages/notifications/notifications.module').then(m => m.NotificationsPageModule),
        resolve: { items: UserNotificationsResolverService, owner: ProducerDetailsResolverService }
      },
      {
        path: "consumer",
        children: [
          {
            path: "",
            loadChildren: () => import('./pages/consumers-producer/consumers-producer.module').then(m => m.ConsumersProducerPageModule),
            resolve: { items: MyConsumersResolverService, owner: ProducerDetailsResolverService }
          },
          {
            path: ":consumerId",
            loadChildren: () => import('./pages/consumer-producer/consumer-producer.module').then(m => m.ConsumerProducerPageModule),
            resolve: {
              item: ConsumerShortdetailsResolverService,
              orders: ProducerConsumerOrdersResolverService,
              owner: ProducerDetailsResolverService
            }
          }
        ]
      },
      {
        path: "payment",
        children: [
          {
            path: "config",
            loadChildren: () => import('./pages/payment-config/payment-config.module').then(m => m.PaymentConfigPageModule),
            resolve: {
              config: PaymentConfigResolverService,
              profile: ProducerDetailsResolverService
            }
          }
        ]
      }
    ]
  },
  {
    path: "market/:marketId",
    resolve: {user:UserResolverService},
    children: [
      {
        path: "",
        loadChildren: () => import('./pages/market-view/market-view.module').then(m => m.MarketViewPageModule),
        resolve: { market: MarketLightResolverService, viewer: ViewerDetailsResolverService, order: OrderResolverService }
      },
      {
        path: ":tourneeId",
        loadChildren: () => import('./pages/tournee-view/tournee-view.module').then(m => m.TourneeViewPageModule),
        resolve: { tournee: TourneeResolverService, order: OrderResolverService, consumer: ConsumerDetailsResolverService }
      }
    ]
  },
  {
    path: "admin/:profileId",
    canActivate: [AdminGuard],
    resolve: {user:UserResolverService},
    data: { role: "admin" },
    children: [
      { path: "", loadChildren: () => import('./pages/home-admin/home-admin.module').then(m => m.HomeAdminPageModule) },
      {
        path: "users-map",
        loadChildren: () => import('./pages/users-map/users-map.module').then(m => m.UsersMapPageModule),
        resolve: { counters: AreaCountersResolverService, cities: CitiesResolverService }
      },
      {
        path: "products",
        loadChildren: () => import('./pages/admin-products/admin-products.module').then(m => m.AdminProductsPageModule),
        resolve: { products: ProductsResolverService }
      },
      {
        path: "users",
        loadChildren: () => import('./pages/admin-users/admin-users.module').then(m => m.AdminUsersPageModule),
        resolve: {
          notifications: NotificationsResolverService,
          consumers: ConsumersResolverService,
          producers: ProducersResolverService,
          users: UsersResolverService
        }
      },
      {
        path: "user",
        loadChildren: () => import('./pages/admin-user/admin-user.module').then(m => m.AdminUserPageModule),
      },
      {
        path: "producers",
        loadChildren: () => import('./pages/producers-public/producers-public.module').then(m => m.ProducersPublicPageModule),
      },
      {
        path: "cities",
        loadChildren: () => import('./pages/admin-cities/admin-cities.module').then(m => m.AdminCitiesPageModule),
        resolve: { cities: CitiesResolverService, counters: AreaCountersResolverService }
      },
      {
        path: "paymentmodes",
        loadChildren: () => import('./pages/admin-paymentmodes/admin-paymentmodes.module').then(m => m.AdminPaymentModesPageModule),
        resolve: { items: PaymentModesResolverService }
      }
    ]
  },
  { path: "settings", loadChildren: () => import('./pages/settings/settings.module').then(m => m.SettingsPageModule) },
  { path: "test", loadChildren: () => import('./pages/test/test.module').then(m => m.TestPageModule) },
  { path: ":link1", canActivate: [LinkGuard], loadChildren: () => import('./pages/oups/oups.module').then(m => m.OupsPageModule) },
  { path: ":link1/:link2", canActivate: [LinkGuard], loadChildren: () => import('./pages/oups/oups.module').then(m => m.OupsPageModule) },
  { path: ":link1/:link2/:link3", canActivate: [LinkGuard], loadChildren: () => import('./pages/oups/oups.module').then(m => m.OupsPageModule) },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules,
    paramsInheritanceStrategy:'always',anchorScrolling: 'enabled', })],
  exports: [RouterModule]
})
export class AppRoutingModule {}
